/**
 * Import Service
 * API calls for bulk data import functionality
 */

import { api } from './api';

export interface ImportError {
  row: number;
  field?: string;
  message: string;
  value?: any;
}

export interface ImportResult {
  success: boolean;
  totalRows: number;
  successCount: number;
  errorCount: number;
  errors: ImportError[];
}

export type ImportType = 'users' | 'objectives' | 'key-results';

/**
 * Download import template
 * @param type - Type of import template
 * @param format - File format (xlsx or csv)
 */
export const downloadTemplate = async (
  type: ImportType,
  format: 'xlsx' | 'csv' = 'xlsx'
): Promise<void> => {
  try {
    const response = await api.get(`/import/template/${type}`, {
      params: { format },
      responseType: 'blob',
    });

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;

    // Set filename
    const timestamp = new Date().toISOString().split('T')[0];
    const typeStr = type.replace('-', '_');
    const extension = format === 'xlsx' ? 'xlsx' : 'csv';
    link.setAttribute('download', `okrhub_${typeStr}_template_${timestamp}.${extension}`);

    // Trigger download
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    throw error;
  }
};

/**
 * Import users from file
 * @param file - File to import
 */
export const importUsers = async (file: File): Promise<ImportResult> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post('/import/users', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data.data as ImportResult;
};

/**
 * Import objectives from file
 * @param file - File to import
 */
export const importObjectives = async (file: File): Promise<ImportResult> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post('/import/objectives', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data.data as ImportResult;
};

/**
 * Import key results from file
 * @param file - File to import
 */
export const importKeyResults = async (file: File): Promise<ImportResult> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post('/import/key-results', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data.data as ImportResult;
};
